import React, { useState, useEffect, useCallback } from 'react';
import { FiShield, FiPlus, FiEdit2, FiCheck, FiX, FiCheckSquare, FiSquare } from 'react-icons/fi';
import api from '../services/api';
import { ALL_MENU_ITEMS, SECTIONS_ORDER, SECTION_LABELS, ALL_KEYS } from '../constants/menuItems';

/* ── Agrupador ──────────────────────────────────────────────────── */
const bySection = ALL_MENU_ITEMS.reduce((acc, item) => {
  if (!acc[item.section]) acc[item.section] = [];
  acc[item.section].push(item);
  return acc;
}, {});

/* ── Estilos base compartidos ───────────────────────────────────── */
const pageStyle = {
  padding: '24px',
  fontFamily: 'inherit',
  maxWidth: 1100,
};

const cardStyle = {
  background: '#fff',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
};

export default function PerfilesPage() {
  const [perfiles, setPerfiles]   = useState([]);
  const [selected, setSelected]   = useState(null); // perfil objeto
  const [items, setItems]         = useState(new Set());
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [msg, setMsg]             = useState(null);  // { type: 'ok'|'err', text }

  /* ── modal nuevo/editar perfil ── */
  const [modal, setModal]  = useState(null); // null | 'new' | 'edit'
  const [form, setForm]    = useState({ nombre: '', descripcion: '', es_admin: false });
  const [formErr, setFormErr] = useState('');

  /* ── cargar lista ── */
  const loadPerfiles = useCallback(async () => {
    try {
      const r = await api.get('/api/perfiles');
      const list = r.data?.data || [];
      setPerfiles(list);
      // Si hay uno seleccionado, actualizar referencia
      setSelected(prev => prev ? (list.find(p => p.id === prev.id) || null) : null);
    } catch {}
  }, []);

  useEffect(() => { loadPerfiles(); }, [loadPerfiles]);

  /* ── cargar items del perfil seleccionado ── */
  useEffect(() => {
    if (!selected) { setItems(new Set()); setDirty(false); return; }
    api.get(`/api/perfiles/${selected.id}/items`)
      .then(r => { setItems(new Set(r.data?.items || [])); setDirty(false); })
      .catch(() => { setItems(new Set()); });
  }, [selected]);

  /* ── toggle item ── */
  const toggle = (key) => {
    if (selected?.es_admin) return; // admin: bloqueado
    setItems(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setDirty(true);
  };

  const markAll = () => {
    if (selected?.es_admin) return;
    setItems(new Set(ALL_KEYS));
    setDirty(true);
  };

  const clearAll = () => {
    if (selected?.es_admin) return;
    setItems(new Set());
    setDirty(true);
  };

  /* ── guardar ── */
  const save = async () => {
    if (!selected || !dirty) return;
    setSaving(true);
    setMsg(null);
    try {
      await api.post(`/api/perfiles/${selected.id}/items`, { keys: [...items] });
      setDirty(false);
      setMsg({ type: 'ok', text: 'Accesos guardados correctamente.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Error al guardar.' });
    } finally {
      setSaving(false);
    }
  };

  /* ── abrir modal ── */
  const openNew  = () => { setForm({ nombre: '', descripcion: '', es_admin: false }); setFormErr(''); setModal('new'); };
  const openEdit = (p) => {
    setForm({ nombre: p.nombre, descripcion: p.descripcion || '', es_admin: !!p.es_admin });
    setFormErr('');
    setModal('edit');
  };

  /* ── guardar perfil (crear/editar) ── */
  const saveForm = async () => {
    if (!form.nombre.trim()) { setFormErr('El nombre es requerido.'); return; }
    setFormErr('');
    try {
      if (modal === 'new') {
        await api.post('/api/perfiles', form);
      } else {
        await api.put(`/api/perfiles/${selected.id}`, form);
      }
      await loadPerfiles();
      setModal(null);
    } catch (e) {
      setFormErr(e.response?.data?.error || 'Error al guardar.');
    }
  };

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
        <FiShield size={22} color="#6366f1" />
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1e293b' }}>Perfiles de Acceso</h2>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── Panel izquierdo: lista de perfiles ── */}
        <div style={{ ...cardStyle, width: 260, flexShrink: 0 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#475569' }}>PERFILES</span>
            <button onClick={openNew} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiPlus size={12} /> Nuevo
            </button>
          </div>
          <div>
            {perfiles.map(p => (
              <div
                key={p.id}
                onClick={() => { if (dirty && selected?.id !== p.id) { if (!window.confirm('Hay cambios sin guardar. ¿Continuar?')) return; } setSelected(p); }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f5f9',
                  background: selected?.id === p.id ? '#ede9fe' : 'transparent',
                  borderLeft: selected?.id === p.id ? '3px solid #6366f1' : '3px solid transparent',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: selected?.id === p.id ? 600 : 400, fontSize: 14, color: '#1e293b' }}>{p.nombre}</div>
                  {p.es_admin ? (
                    <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px', marginTop: 2, display: 'inline-block' }}>ADMIN</span>
                  ) : null}
                </div>
                {selected?.id === p.id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', padding: 4 }}
                    title="Editar perfil"
                  >
                    <FiEdit2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Panel derecho: checkboxes ── */}
        <div style={{ flex: 1, ...cardStyle }}>
          {!selected ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              Selecciona un perfil para configurar sus accesos
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{selected.nombre}</span>
                  {!!selected.es_admin && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px' }}>
                      Perfil administrador — acceso total
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {!selected.es_admin && (
                    <>
                      <button onClick={markAll} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
                        <FiCheckSquare size={12} /> Todos
                      </button>
                      <button onClick={clearAll} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
                        <FiSquare size={12} /> Ninguno
                      </button>
                    </>
                  )}
                  <button
                    onClick={save}
                    disabled={!dirty || saving || !!selected.es_admin}
                    style={{
                      fontSize: 12, padding: '4px 14px', border: 'none', borderRadius: 6,
                      background: dirty && !selected.es_admin ? '#6366f1' : '#e2e8f0',
                      color: dirty && !selected.es_admin ? '#fff' : '#94a3b8',
                      cursor: dirty && !selected.es_admin ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600,
                    }}
                  >
                    {saving ? 'Guardando…' : <><FiCheck size={12} /> Guardar</>}
                  </button>
                </div>
              </div>

              {/* Mensaje */}
              {msg && (
                <div style={{ margin: '10px 20px 0', padding: '8px 14px', borderRadius: 6, fontSize: 13,
                  background: msg.type === 'ok' ? '#dcfce7' : '#fee2e2',
                  color:      msg.type === 'ok' ? '#166534' : '#991b1b',
                }}>
                  {msg.text}
                </div>
              )}

              {/* Grid de secciones */}
              <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                {SECTIONS_ORDER.map(sec => {
                  const sectionItems = bySection[sec];
                  if (!sectionItems) return null;
                  return (
                    <div key={sec} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ background: '#f8fafc', padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {SECTION_LABELS[sec]}
                      </div>
                      <div>
                        {sectionItems.map(item => {
                          const checked = selected.es_admin ? true : items.has(item.key);
                          return (
                            <label
                              key={item.key}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '7px 12px',
                                cursor: selected.es_admin ? 'default' : 'pointer',
                                borderTop: '1px solid #f1f5f9',
                                background: checked ? '#f0fdf4' : '#fff',
                                transition: 'background 0.15s',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!!selected.es_admin}
                                onChange={() => toggle(item.key)}
                                style={{ cursor: selected.es_admin ? 'default' : 'pointer', accentColor: '#6366f1' }}
                              />
                              <span style={{ fontSize: 13, color: '#374151' }}>{item.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modal nuevo/editar perfil ── */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{modal === 'new' ? 'Nuevo Perfil' : 'Editar Perfil'}</h3>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Nombre *</label>
                <input
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, boxSizing: 'border-box' }}
                  placeholder="Ej: Vendedor, Cajero..."
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Descripción</label>
                <textarea
                  value={form.descripcion}
                  onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  rows={2}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.es_admin}
                  onChange={e => setForm(f => ({ ...f, es_admin: e.target.checked }))}
                  style={{ accentColor: '#6366f1' }}
                />
                Perfil administrador (acceso total al sistema)
              </label>
              {formErr && <div style={{ fontSize: 12, color: '#dc2626', background: '#fee2e2', padding: '6px 10px', borderRadius: 6 }}>{formErr}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setModal(null)} style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
              <button onClick={saveForm} style={{ padding: '7px 18px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
